import React, { useEffect, useState } from "react";
import { api, fmtMoney, fmtNumber, fmtDateTime, STAGE_ORDER, ROLE_LABELS } from "@/lib/apiClient";
import { useAuth } from "@/lib/auth";
import { useFx } from "@/lib/fx";
import StatusBadge from "@/components/StatusBadge";
import ViewInCurrency from "@/components/ViewInCurrency";
import { Link } from "react-router-dom";
import { ArrowUpRight, TrendUp, CheckSquare, Coins, Megaphone, Tray, PaperPlaneTilt } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Dashboard() {
    const { user } = useAuth();
    const { base: fxBase, toBase } = useFx();
    const [stats, setStats] = useState(null);
    const [inbox, setInbox] = useState([]);
    const [loading, setLoading] = useState(true);

    const isExecution = user?.role === "execution" || user?.role === "execution_head";
    const isDelivery = user?.role === "delivery" || user?.role === "delivery_head";

    useEffect(() => {
        (async () => {
            try {
                const requests = [api.get("/dashboard/stats")];
                if (isExecution) {
                    requests.push(api.get("/campaigns?stage=intake"));
                } else if (isDelivery) {
                    requests.push(api.get("/campaigns?stage=execution"));
                }
                const results = await Promise.all(requests);
                setStats(results[0].data);
                if (results[1]) {
                    let list = results[1].data || [];
                    if (isDelivery) list = list.filter((c) => c.ready_for_delivery);
                    setInbox(list);
                }
            } catch (e) {
                setStats(null);
            } finally {
                setLoading(false);
            }
        })();
    }, [isExecution, isDelivery]);

    if (loading) return <div className="font-mono text-xs text-slate-400">loading…</div>;

    const canSeeRevenue = ["super_admin", "accounts", "client_servicing"].includes(user?.role);

    const totalCampaigns = stats?.total_campaigns ?? 0;
    const revenue = stats?.revenue?.total_revenue ?? 0;
    const payout = stats?.revenue?.total_payout ?? 0;
    const profit = revenue - payout;
    const pending = stats?.pending_approvals ?? 0;

    return (
        <div className="space-y-10">
            {/* Header */}
            <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
                <div>
                    <div className="overline mb-1">// Workspace</div>
                    <h1 className="font-display text-4xl md:text-5xl tracking-tighter">Operations Dashboard</h1>
                    <p className="text-slate-500 mt-2 text-sm">
                        Welcome back, <span className="text-slate-900 font-medium">{user?.name}</span>
                        <span className="mx-2 text-slate-300">/</span>
                        <span className="font-mono text-xs uppercase tracking-wider">{ROLE_LABELS[user?.role]}</span>
                    </p>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                    {canSeeRevenue && <ViewInCurrency testid="dashboard-view-in" />}
                    <Link
                        to="/campaigns"
                        data-testid="dashboard-view-campaigns"
                        className="bg-white border border-slate-200 hover:border-slate-900 px-4 py-2.5 text-sm font-medium inline-flex items-center gap-2"
                    >
                        View Campaigns <ArrowUpRight size={14} />
                    </Link>
                </div>
            </header>

            {/* KPI strip */}
            <section
                data-testid="dashboard-kpi-strip"
                className={`grid gap-px bg-slate-200 border border-slate-200 ${canSeeRevenue ? "grid-cols-2 lg:grid-cols-4" : "grid-cols-1 md:grid-cols-3"}`}
            >
                <Kpi icon={Megaphone} label="Total campaigns" value={fmtNumber(totalCampaigns)} sub="all stages" testid="kpi-total-campaigns" />
                {canSeeRevenue && (
                    <Kpi icon={Coins} label={`Revenue · in ${fxBase}`} value={fmtMoney(toBase(revenue, "USD").amount, fxBase)} sub={`profit ${fmtMoney(toBase(profit, "USD").amount, fxBase)}`} testid="kpi-revenue" />
                )}
                <Kpi icon={CheckSquare} label="Pending approvals" value={fmtNumber(pending)} sub="reports awaiting review" testid="kpi-pending-approvals" />
                <Kpi icon={TrendUp} label="Active platforms" value="DV360 · Meta · TikTok" sub="multi-platform" testid="kpi-platforms" />
            </section>

            {(isExecution || isDelivery) && (
                <ExecutionInbox
                    items={inbox}
                    isExecution={isExecution}
                    onPushed={() => {
                        // refetch inbox after a push
                        api.get(isExecution ? "/campaigns?stage=intake" : "/campaigns?stage=execution")
                            .then((r) => {
                                let list = r.data || [];
                                if (isDelivery) list = list.filter((c) => c.ready_for_delivery);
                                setInbox(list);
                            })
                            .catch(() => {});
                    }}
                />
            )}

            <div className={`grid gap-px bg-slate-200 border border-slate-200 ${canSeeRevenue ? "grid-cols-1 lg:grid-cols-3" : "grid-cols-1"}`}>
                {/* Stage breakdown */}
                <section className={`bg-white p-6 ${canSeeRevenue ? "lg:col-span-2" : ""}`}>
                    <div className="flex items-center justify-between mb-5">
                        <div>
                            <div className="overline">Pipeline</div>
                            <h2 className="font-display text-xl mt-1">Campaigns by Stage</h2>
                        </div>
                        <Link to="/campaigns" className="text-xs font-mono uppercase tracking-wider text-[var(--c-accent)] hover:underline">View Kanban →</Link>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-px bg-slate-200 border border-slate-200">
                        {STAGE_ORDER.map((s) => (
                            <div key={s} className="bg-white p-4" data-testid={`stage-card-${s}`}>
                                <StatusBadge status={s} />
                                <div className="font-mono text-3xl tracking-tight mt-3">{stats?.by_stage?.[s] ?? 0}</div>
                            </div>
                        ))}
                    </div>
                </section>

                {/* Top clients — revenue roles only */}
                {canSeeRevenue && (
                    <section className="bg-white p-6">
                        <div className="overline mb-1">Revenue</div>
                        <h2 className="font-display text-xl mb-5">Top Clients</h2>
                        <div className="divide-y divide-slate-200">
                            {(stats?.top_clients || []).map((c, i) => (
                                <div key={c._id} className="flex items-center justify-between py-3" data-testid={`top-client-${i}`}>
                                    <div className="flex items-center gap-3 min-w-0">
                                        <span className="font-mono text-xs text-slate-400">{String(i + 1).padStart(2, "0")}</span>
                                        <div className="min-w-0">
                                            <div className="text-sm font-medium truncate">{c.client_name}</div>
                                            <div className="text-[11px] font-mono text-slate-500">{c.count} campaigns</div>
                                        </div>
                                    </div>
                                    <div className="font-mono text-sm">{fmtMoney(toBase(c.revenue, "USD").amount, fxBase)}</div>
                                </div>
                            ))}
                            {(stats?.top_clients || []).length === 0 && (
                                <div className="text-sm text-slate-500 py-6 text-center">No data yet.</div>
                            )}
                        </div>
                    </section>
                )}
            </div>
        </div>
    );
}

function Kpi({ icon: Icon, label, value, sub, testid }) {
    return (
        <div
            data-testid={testid}
            className="bg-white p-6 animate-fade-up"
        >
            <div className="flex items-center justify-between mb-4">
                <span className="overline">{label}</span>
                <Icon size={18} className="text-slate-400" />
            </div>
            <div className="font-mono text-3xl md:text-4xl tracking-tighter">{value}</div>
            <div className="text-xs text-slate-500 mt-1">{sub}</div>
        </div>
    );
}

function ExecutionInbox({ items, isExecution, onPushed }) {
    const [draggingId, setDraggingId] = useState(null);
    const [overDrop, setOverDrop] = useState(false);

    const onDragStart = (e, c) => {
        if (!isExecution) return;
        setDraggingId(c.id);
        e.dataTransfer.effectAllowed = "move";
        e.dataTransfer.setData("text/plain", c.id);
    };
    const onDragEnd = () => { setDraggingId(null); setOverDrop(false); };
    const onDropDelivery = async (e) => {
        e.preventDefault();
        const id = e.dataTransfer.getData("text/plain") || draggingId;
        setOverDrop(false);
        setDraggingId(null);
        if (!id) return;
        try {
            await api.post(`/campaigns/${id}/push-to-delivery`);
            toast.success("Pushed to Delivery Head — notification sent");
            onPushed?.();
        } catch (err) {
            const msg = err.response?.data?.detail;
            if (typeof msg === "string" && msg.toLowerCase().includes("save the execution plan")) {
                toast.error("Save the execution plan first — open the campaign.");
            } else {
                toast.error(msg || err.message);
            }
        }
    };

    return (
        <section className="grid grid-cols-1 lg:grid-cols-3 gap-px bg-slate-200 border border-slate-200" data-testid="execution-inbox">
            <div className="lg:col-span-2 bg-white">
                <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
                    <div className="flex items-center gap-3">
                        <Tray size={18} className="text-slate-500" />
                        <div>
                            <div className="overline">
                                {isExecution ? "Execution inbox" : "Delivery inbox"}
                            </div>
                            <h2 className="font-display text-lg">
                                {isExecution
                                    ? "Campaigns awaiting execution planning"
                                    : "Campaigns pushed for delivery planning"}
                            </h2>
                        </div>
                    </div>
                    <span className="font-mono text-xs text-slate-500">{items.length} in queue</span>
                </div>
                {items.length === 0 ? (
                    <div className="px-6 py-10 text-center text-sm text-slate-500" data-testid="execution-inbox-empty">
                        All caught up — no new campaigns in your queue.
                    </div>
                ) : (
                    <ul className="divide-y divide-slate-100 max-h-[480px] overflow-y-auto scrollbar-thin">
                        {items.slice(0, 30).map((c) => (
                            <li
                                key={c.id}
                                draggable={isExecution}
                                onDragStart={(e) => onDragStart(e, c)}
                                onDragEnd={onDragEnd}
                                className={`px-6 py-3 flex items-center justify-between gap-4 ${isExecution ? "cursor-grab active:cursor-grabbing" : ""} ${draggingId === c.id ? "opacity-40" : ""}`}
                                data-testid={`inbox-item-${c.id}`}
                            >
                                <div className="min-w-0">
                                    <div className="overline truncate">{c.client_org_name || "—"}</div>
                                    <div className="text-sm font-medium truncate">{c.name}</div>
                                    <div className="text-[11px] font-mono text-slate-500 mt-0.5 flex items-center gap-2 flex-wrap">
                                        <span>{fmtNumber(c.volume)} impressions · ends {c.end_date}</span>
                                        {c.sla?.applicable && <SlaBadge sla={c.sla} />}
                                    </div>
                                </div>
                                <Link
                                    to={`/campaigns/${c.id}`}
                                    data-testid={`inbox-open-${c.id}`}
                                    className="text-xs font-mono uppercase tracking-wider text-[var(--c-accent)] hover:underline inline-flex items-center gap-1 whitespace-nowrap"
                                >
                                    Open {isExecution ? "execution" : "delivery"} <ArrowUpRight size={12} />
                                </Link>
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            {isExecution && (
                <div
                    onDragOver={(e) => { e.preventDefault(); setOverDrop(true); }}
                    onDragLeave={() => setOverDrop(false)}
                    onDrop={onDropDelivery}
                    data-testid="dnd-push-delivery-zone"
                    className={`bg-white flex flex-col items-center justify-center p-6 text-center transition-colors min-h-[200px] border-2 border-dashed ${
                        overDrop ? "border-slate-900 bg-blue-50" : "border-slate-200"
                    }`}
                >
                    <PaperPlaneTilt size={28} className={overDrop ? "text-slate-900" : "text-slate-400"} weight={overDrop ? "fill" : "regular"} />
                    <div className="overline mt-3">Drop zone</div>
                    <h3 className="font-display text-lg mt-0.5">Push to Delivery Head</h3>
                    <p className="text-xs text-slate-500 mt-2 max-w-[220px]">
                        Drag any campaign from the inbox here to hand off to the Delivery team — once its execution plan is saved.
                    </p>
                </div>
            )}
        </section>
    );
}

function SlaBadge({ sla }) {
    if (!sla) return null;
    const styles = {
        breached: { color: "#B91C1C", bg: "#FEE2E2", border: "#FCA5A5" },
        at_risk: { color: "#B45309", bg: "#FEF3C7", border: "#FCD34D" },
        on_track: { color: "#15803D", bg: "#DCFCE7", border: "#86EFAC" },
    };
    const m = styles[sla.risk];
    if (!m) return null;
    const hrs = sla.breach ? sla.breach_severity_hours : sla.hours_remaining;
    const label = sla.breach ? "over SLA" : "left";
    return (
        <span
            className="font-mono text-[10px] uppercase tracking-wider px-1.5 py-0.5"
            style={{ color: m.color, backgroundColor: m.bg, border: `1px solid ${m.border}` }}
        >
            {Math.round(Math.abs(hrs))}h {label}
        </span>
    );
}
